import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Divider,
} from "@mui/material";
import { isVendedor, getUserContext } from "../../auth";
import { apiFetch } from "../../api";

function renderValue(v) {
  if (v === null || v === undefined || v === "") return "";
  if (typeof v === "string" || typeof v === "number" || typeof v === "boolean")
    return String(v);
  if (Array.isArray(v)) return v.map((x) => renderValue(x)).join(", ");
  try {
    return JSON.stringify(v, null, 2);
  } catch (e) {
    return String(v);
  }
}

function formatDate(dateStr) {
  if (!dateStr) return "";
  // Parse date from "YYYY-MM-DDTHH:mm:ss" format to "DD/MM/YYYY"
  const match = String(dateStr).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) {
    return `${match[3]}/${match[2]}/${match[1]}`;
  }
  return String(dateStr);
}

function renderClientCard(obj) {
  if (!obj) return <Typography variant="body2">—</Typography>;

  const labels = {
    telefono: "Teléfono",
    email: "E-mail",
    consulta: "Consulta 1",
    consulta2: "Consulta 2",
    entrega: "Entrega",
    precio_de_toma: "Precio toma",
    obtenido_de: "Obtenido de",
    fecha_carga: "Fecha de carga",
  };

  // Define rows with 2 attributes each
  const rows = [
    ["telefono", "email"],
    ["consulta", "consulta2"],
    ["entrega", "precio_de_toma"],
    ["obtenido_de", "fecha_carga"],
  ];

  const getValue = (key) => {
    const v = obj[key];
    if (key === "fecha_carga") return formatDate(v);
    return renderValue(v);
  };

  return (
    <Box sx={{ width: "100%" }}>
      {rows.map((row, rowIndex) => (
        <Box
          key={rowIndex}
          sx={{
            display: "flex",
            gap: 2,
            mb: 0.5,
          }}
        >
          {row.map((key) => (
            <Box key={key} sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="caption"
                sx={{ color: "text.secondary", fontSize: "0.75rem" }}
              >
                {labels[key] ?? key}:
              </Typography>{" "}
              <Typography
                component="span"
                variant="body2"
                sx={{ fontSize: "0.85rem", wordBreak: "break-word" }}
              >
                {getValue(key)}
              </Typography>
            </Box>
          ))}
        </Box>
      ))}
    </Box>
  );
}

export default function DuplicateResolveDialog({
  open,
  onClose,
  onCancelAll,
  onSkip,
  existing,
  incoming,
  match_source,
  onResolved,
  queueIndex = 0,
  queueTotal = 0,
  remaining = 0,
}) {
  const [selected, setSelected] = React.useState("incoming_no_transfer");
  const [loading, setLoading] = React.useState(false);

  // support existing being either a single object or a list
  const existingList = React.useMemo(() => {
    if (!existing) return [];
    return Array.isArray(existing) ? existing : [existing];
  }, [existing]);

  // For transfer mode: first selected client is transfer target (green), rest are to delete (red)
  const [transferTargetId, setTransferTargetId] = React.useState(null);
  const [deleteIds, setDeleteIds] = React.useState(new Set());
  
  // For non-transfer mode: selected clients will be deleted
  const [selectedForDelete, setSelectedForDelete] = React.useState(new Set());

  React.useEffect(() => {
    if (!open) return;
    setSelected("incoming_no_transfer");
    setLoading(false);
    // Reset all selections
    setTransferTargetId(null);
    setDeleteIds(new Set());
    setSelectedForDelete(new Set());
  }, [open, existing?.id]);

  const currentUser = getUserContext();
  const isSeller = isVendedor();
  const currentVendedorId =
    currentUser?.vendedor_id ?? currentUser?.id_vendedor ?? null;

  // Check if a client belongs to the current seller
  const clientBelongsToSeller = (clientId) => {
    if (!isSeller) return true; // Admin can select any client
    const client = existingList.find((c) => c.id === clientId);
    return client?.vendedor?.id === currentVendedorId;
  };

  // Handle client card click based on current mode
  const handleClientClick = (clientId) => {
    // If seller and client doesn't belong to them, do nothing
    if (isSeller && !clientBelongsToSeller(clientId)) {
      return;
    }

    if (selected === "incoming_transfer") {
      // Transfer mode: first click sets transfer target, subsequent clicks toggle delete
      if (transferTargetId === null) {
        // First selection = transfer target (green)
        setTransferTargetId(clientId);
      } else if (transferTargetId === clientId) {
        // Clicking the transfer target again deselects everything
        setTransferTargetId(null);
        setDeleteIds(new Set());
      } else {
        // Toggle in delete set (red)
        const copy = new Set(deleteIds);
        if (copy.has(clientId)) {
          copy.delete(clientId);
        } else {
          copy.add(clientId);
        }
        setDeleteIds(copy);
      }
    } else {
      // Non-transfer mode: toggle client in delete set
      const copy = new Set(selectedForDelete);
      if (copy.has(clientId)) {
        copy.delete(clientId);
      } else {
        copy.add(clientId);
      }
      setSelectedForDelete(copy);
    }
  };

  // Get client card style based on selection state
  const getClientCardStyle = (clientId) => {
    const canSelect = clientBelongsToSeller(clientId);
    
    const baseStyle = {
      mb: 1.5,
      p: 1.5,
      borderRadius: 2,
      cursor: canSelect ? 'pointer' : 'not-allowed',
      transition: 'all 0.2s ease',
      opacity: canSelect ? 1 : 0.6,
      '&:hover': canSelect ? {
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
      } : {},
    };

    if (selected === "incoming_transfer") {
      if (transferTargetId === clientId) {
        // Transfer target = green
        return {
          ...baseStyle,
          background: 'rgba(76, 175, 80, 0.15)',
          border: '2px solid #4caf50',
        };
      } else if (deleteIds.has(clientId)) {
        // To delete = red
        return {
          ...baseStyle,
          background: 'rgba(244, 67, 54, 0.15)',
          border: '2px solid #f44336',
        };
      }
    } else {
      if (selectedForDelete.has(clientId)) {
        // Selected for delete = red
        return {
          ...baseStyle,
          background: 'rgba(244, 67, 54, 0.15)',
          border: '2px solid #f44336',
        };
      }
    }

    // Not selected
    return {
      ...baseStyle,
      background: 'rgba(0,0,0,0.03)',
      border: '1px solid rgba(0,0,0,0.08)',
    };
  };

  // Check if at least one existing client belongs to the current seller
  const hasOwnClient = existingList.some((c) => c.vendedor?.id === currentVendedorId);

  // canTransfer: show the 'transfer assignment' option only when the match_source
  // indicates the existing client is assigned ACTIVE and either the user is
  // admin or the seller has at least one client they can transfer from.
  const canTransfer =
    match_source === "ASIGNADO_ACTIVO" &&
    (!isSeller || hasOwnClient);

  const actionCardStyle = (action) => ({
    borderRadius: 2,
    p: 1.5,
    cursor: "pointer",
    background:
      selected === action ? "rgba(25,118,210,0.16)" : "rgba(25,118,210,0.06)",
    border: selected === action ? "2px solid #1976d2" : "2px solid transparent",
    transition: "all 0.2s ease",
    "&:hover": {
      background: "rgba(25,118,210,0.12)",
    },
  });

  // Reset selections when switching modes
  const handleModeChange = (newMode) => {
    setSelected(newMode);
    // Clear all selections when switching modes
    setTransferTargetId(null);
    setDeleteIds(new Set());
    setSelectedForDelete(new Set());
  };

  const handleContinue = async () => {
    const transfer = selected === "incoming_transfer";

    // Build delete_ids based on mode
    let delete_ids = [];
    let transferFromId = null;

    if (transfer) {
      // Transfer mode: transferTargetId is the source, deleteIds are additional deletions
      transferFromId = transferTargetId;
      
      if (!transferFromId) {
        alert("Debes seleccionar el cliente desde el cual transferir la asignación (se marcará en verde)");
        return;
      }
      
      // Validate that seller can only select clients that belong to them
      if (isSeller) {
        if (!clientBelongsToSeller(transferFromId)) {
          alert("No podés transferir desde un cliente que no te pertenece.");
          return;
        }
        for (const id of deleteIds) {
          if (!clientBelongsToSeller(id)) {
            alert("No podés eliminar clientes que no te pertenecen.");
            return;
          }
        }
      }
      
      // Transfer mode: 
      // - transfer_from_id is the client to RENAME (green) - NOT deleted, just renamed
      // - delete_ids contains only additional clients to delete (red)
      // The green client keeps its checklist, devoluciones, etc. - only its name changes
      delete_ids = Array.from(deleteIds);  // Only the additional red ones
    } else {
      // Non-transfer mode: selectedForDelete are clients to delete
      delete_ids = Array.from(selectedForDelete);
      
      // Validate that seller can only delete clients that belong to them
      if (isSeller && delete_ids.length > 0) {
        for (const id of delete_ids) {
          if (!clientBelongsToSeller(id)) {
            alert("No podés eliminar clientes que no te pertenecen.");
            return;
          }
        }
      }
    }

    let confirmMsg = "";
    if (transfer) {
      const additionalDeletes = deleteIds.size;
      if (additionalDeletes === 0) {
        confirmMsg = `Se actualizará el cliente existente con los datos del nuevo cliente (excepto el teléfono).\n¿Continuar?`;
      } else {
        confirmMsg = `Se creará el nuevo cliente, se transferirá la asignación y se eliminarán ${additionalDeletes} cliente(s) adicional(es) marcado(s) en rojo.\n¿Continuar?`;
      }
    } else {
      if (delete_ids.length === 0) {
        confirmMsg = `Se creará el nuevo cliente sin eliminar los existentes.\n¿Continuar?`;
      } else {
        confirmMsg = `Se creará el nuevo cliente y se eliminarán ${delete_ids.length} cliente(s) seleccionado(s).\n¿Continuar?`;
      }
    }

    if (!window.confirm(confirmMsg)) return;

    setLoading(true);
    try {
      // If seller is creating the client, ensure the client is assigned to them
      const incomingWithVendedor = { ...incoming };
      if (isSeller && currentVendedorId && !transfer) {
        incomingWithVendedor.vendedor_id = currentVendedorId;
      }

      const payload = {
        action: "keep_incoming",
        delete_ids: delete_ids,
        incoming: incomingWithVendedor,
        transfer_assignment: transfer,
        transfer_from_id: transfer ? transferFromId : undefined,
      };
      const res = await apiFetch("/api/clientes/resolve-duplicate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        credentials: "include",
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || "Error resolviendo duplicado");
      }
      const data = await res.json();

      onResolved && onResolved({ kept: "incoming", created: data });
      return;
    } catch (err) {
      console.error("resolve duplicate error", err);
      alert(err.message || "Error al resolver duplicado");
    } finally {
      setLoading(false);
    }
  };

  if ((!existingList || existingList.length === 0) || !incoming) return null;

  const closeAll = (reason) => {
    // ESC / click afuera: si hay cola -> onCancelAll, si no -> onClose
    if (onCancelAll) return onCancelAll(reason);
    if (onClose) return onClose(reason);
  };

  const skipOne = () => {
    // Botón "Cancelar": si hay cola -> onSkip, si no -> onClose
    if (onSkip) return onSkip();
    if (onClose) return onClose();
    if (onCancelAll) return onCancelAll();
  };

  return (
    <Dialog
      open={open}
      onClose={(e, reason) => closeAll(reason)}
      maxWidth="md"
      fullWidth
      sx={{ "& .MuiDialog-paper": { overflow: "hidden" } }}
    >
      <DialogTitle sx={{ pt: 0.5, pb: 0.5, fontSize: "1.25rem" }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
          <Box>Conflicto: cliente con número duplicado</Box>

          {queueTotal > 0 && (
            <Box sx={{ fontSize: "0.9rem", color: "text.secondary" }}>
              <strong>Restantes:</strong> {remaining}{" "}
              <span style={{ opacity: 0.7 }}>
                ( {Math.min(queueIndex + 1, queueTotal)} / {queueTotal} )
              </span>
            </Box>
          )}
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: 0, pt: 0, fontSize: "16px" }}>
        {/* If transfer is allowed, show transfer option. Otherwise just show create option. */}
        {canTransfer ? (
          <Box sx={{ display: "flex", gap: 2, p: 1, minHeight: 200 }}>
            <Box sx={{ flex: 1, minWidth: 280 }}>
              {/* Left: actions/options */}
              <Typography variant="subtitle2" sx={{ color: "#0d47a1", fontSize: "0.9rem", height: 24, display: 'flex', alignItems: 'center' }}>
                Acciones
              </Typography>
              <Divider sx={{ mb: 1.5 }} />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box onClick={() => handleModeChange("incoming_no_transfer")} sx={actionCardStyle("incoming_no_transfer")}>
                  <Typography sx={{ fontWeight: 700, mb: 0.5, fontSize: "0.95rem" }}>
                    Crear nuevo cliente
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.8rem" }}>
                    Si no seleccionás ningún cliente de la lista, se creará el nuevo sin eliminar los existentes.
                    Si seleccionás alguno, ese cliente será eliminado.
                  </Typography>
                </Box>

                <Box onClick={() => handleModeChange("incoming_transfer")} sx={actionCardStyle("incoming_transfer")}>
                  <Typography sx={{ fontWeight: 700, mb: 0.5, fontSize: "0.95rem" }}>
                    Transferir
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.8rem" }}>
                    El primer cliente que selecciones (verde) será el origen de la transferencia.
                    Los siguientes que selecciones (rojo) serán eliminados.
                  </Typography>
                </Box>

                {/* Legend for transfer mode */}
                {selected === "incoming_transfer" && (
                  <Box sx={{ mt: 1, p: 1, background: 'rgba(0,0,0,0.02)', borderRadius: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                      Leyenda:
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Box sx={{ width: 16, height: 16, borderRadius: 1, background: 'rgba(76, 175, 80, 0.3)', border: '2px solid #4caf50' }} />
                      <Typography variant="caption">Transferir asignación desde este cliente</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 16, height: 16, borderRadius: 1, background: 'rgba(244, 67, 54, 0.3)', border: '2px solid #f44336' }} />
                      <Typography variant="caption">Será eliminado</Typography>
                    </Box>
                    {isSeller && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        <Box sx={{ width: 16, height: 16, borderRadius: 1, background: 'rgba(0,0,0,0.05)', border: '1px solid rgba(0,0,0,0.2)', opacity: 0.6 }} />
                        <Typography variant="caption">No te pertenece (no se puede seleccionar)</Typography>
                      </Box>
                    )}
                  </Box>
                )}

                {/* Legend for non-transfer mode */}
                {selected === "incoming_no_transfer" && (
                  <Box sx={{ mt: 1, p: 1, background: 'rgba(0,0,0,0.02)', borderRadius: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                      Leyenda:
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 16, height: 16, borderRadius: 1, background: 'rgba(244, 67, 54, 0.3)', border: '2px solid #f44336' }} />
                      <Typography variant="caption">Será eliminado</Typography>
                    </Box>
                    {isSeller && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        <Box sx={{ width: 16, height: 16, borderRadius: 1, background: 'rgba(0,0,0,0.05)', border: '1px solid rgba(0,0,0,0.2)', opacity: 0.6 }} />
                        <Typography variant="caption">No te pertenece (no se puede seleccionar)</Typography>
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            </Box>

            <Box sx={{ flex: 1.5 }}>
              {/* Right: existing list (scrollable) */}
              <Typography variant="subtitle2" sx={{ color: "#0d47a1", fontSize: "0.9rem", height: 24, display: 'flex', alignItems: 'center' }}>
                Clientes existentes
              </Typography>
              <Divider sx={{ mb: 1.5 }} />
              <Box sx={{ maxHeight: 400, overflowY: 'auto', pr: 1 }}>
                {existingList.map((ex) => (
                  <Box 
                    key={ex.id} 
                    onClick={() => handleClientClick(ex.id)}
                    sx={getClientCardStyle(ex.id)}
                  >
                    {/* Header row: client name, vendor */}
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1, flexWrap: 'wrap' }}>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.95rem' }}>
                        Cliente: {ex.nombre}
                      </Typography>
                      <Typography sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
                        pertenece a <strong>{ex.vendedor?.nombre ?? ''}</strong>
                      </Typography>
                      {/* Status indicator */}
                      {selected === "incoming_transfer" && transferTargetId === ex.id && (
                        <Typography sx={{ ml: 'auto', color: '#4caf50', fontWeight: 600, fontSize: '0.8rem' }}>
                          ● Transferir desde aquí
                        </Typography>
                      )}
                      {selected === "incoming_transfer" && deleteIds.has(ex.id) && (
                        <Typography sx={{ ml: 'auto', color: '#f44336', fontWeight: 600, fontSize: '0.8rem' }}>
                          ● Será eliminado
                        </Typography>
                      )}
                      {selected === "incoming_no_transfer" && selectedForDelete.has(ex.id) && (
                        <Typography sx={{ ml: 'auto', color: '#f44336', fontWeight: 600, fontSize: '0.8rem' }}>
                          ● Será eliminado
                        </Typography>
                      )}
                    </Box>
                    {/* Client details in 2-column rows */}
                    {renderClientCard(ex)}
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        ) : (
          <Box sx={{ display: "flex", gap: 2, p: 1, minHeight: 200 }}>
            <Box sx={{ flex: 1, minWidth: 280 }}>
              {/* Left: actions/options */}
              <Typography variant="subtitle2" sx={{ color: "#0d47a1", fontSize: "0.9rem", height: 24, display: 'flex', alignItems: 'center' }}>
                Acciones
              </Typography>
              <Divider sx={{ mb: 1.5 }} />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box onClick={() => handleModeChange("incoming_no_transfer")} sx={actionCardStyle("incoming_no_transfer")}>
                  <Typography sx={{ fontWeight: 700, mb: 0.5, fontSize: "0.95rem" }}>
                    Crear nuevo cliente
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.8rem" }}>
                    Si no seleccionás ningún cliente de la lista, se creará el nuevo sin eliminar los existentes.
                    Si seleccionás alguno, ese cliente será eliminado.
                  </Typography>
                </Box>

                {/* Legend */}
                <Box sx={{ mt: 1, p: 1, background: 'rgba(0,0,0,0.02)', borderRadius: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                    Leyenda:
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 16, height: 16, borderRadius: 1, background: 'rgba(244, 67, 54, 0.3)', border: '2px solid #f44336' }} />
                    <Typography variant="caption">Será eliminado</Typography>
                  </Box>
                  {isSeller && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                      <Box sx={{ width: 16, height: 16, borderRadius: 1, background: 'rgba(0,0,0,0.05)', border: '1px solid rgba(0,0,0,0.2)', opacity: 0.6 }} />
                      <Typography variant="caption">No te pertenece (no se puede seleccionar)</Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>

            <Box sx={{ flex: 1.5 }}>
              {/* Right: existing list (scrollable) */}
              <Typography variant="subtitle2" sx={{ color: "#0d47a1", fontSize: "0.9rem", height: 24, display: 'flex', alignItems: 'center' }}>
                Clientes existentes
              </Typography>
              <Divider sx={{ mb: 1.5 }} />
              <Box sx={{ maxHeight: 400, overflowY: 'auto', pr: 1 }}>
                {existingList.map((ex) => (
                  <Box 
                    key={ex.id} 
                    onClick={() => handleClientClick(ex.id)}
                    sx={getClientCardStyle(ex.id)}
                  >
                    {/* Header row: client name, vendor */}
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1, flexWrap: 'wrap' }}>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.95rem' }}>
                        Cliente: {ex.nombre}
                      </Typography>
                      <Typography sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
                        pertenece a <strong>{ex.vendedor?.nombre ?? ''}</strong>
                      </Typography>
                      {/* Status indicator */}
                      {selectedForDelete.has(ex.id) && (
                        <Typography sx={{ ml: 'auto', color: '#f44336', fontWeight: 600, fontSize: '0.8rem' }}>
                          ● Será eliminado
                        </Typography>
                      )}
                    </Box>
                    {/* Client details in 2-column rows */}
                    {renderClientCard(ex)}
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ pr: 3, pb: 2 }}>
        <Button onClick={skipOne} disabled={loading}>
          Cancelar
        </Button>
        <Button variant="contained" onClick={handleContinue} disabled={loading}>
          {selected === "incoming_transfer" ? "Transferir" : "Crear"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
